import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer, Views, View } from 'react-big-calendar';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/de';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Plus, RefreshCw, Share2, Bug, X as XIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiService, Appointment, GoogleCalendarEvent } from '../../services/apiService';
import AppointmentModal from './AppointmentModal';
import AppointmentForm from './AppointmentForm';
import GoogleEventModal from './GoogleEventModal';
import PageHeader from './ui/PageHeader';

moment.locale('de');

const localizer = momentLocalizer(moment);

type AppointmentEvent = {
  id: string;
  kind: 'appt';
  title: string;
  start: Date;
  end: Date;
  resource: Appointment;
};
type GoogleEvent = {
  id: string;
  kind: 'google';
  title: string;
  start: Date;
  end: Date;
  gevent: GoogleCalendarEvent;
};
type MergedEvent = AppointmentEvent | GoogleEvent;

// --- Custom react-big-calendar sub-components ---

const CalendarToolbar: React.FC<any> = ({ label, onNavigate, onView, view }) => {
  const viewBtn = (v: View, l: string, lShort: string) => (
    <button
      key={v}
      onClick={() => onView(v)}
      aria-pressed={view === v}
      className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-md transition-colors ${
        view === v
          ? 'bg-brand-primary text-white shadow-sm'
          : 'text-brand-text-muted hover:text-brand-text hover:bg-brand-surface-2'
      }`}
    >
      <span className="sm:hidden">{lShort}</span>
      <span className="hidden sm:inline">{l}</span>
    </button>
  );

  return (
    <div className="flex items-center justify-between gap-2 mb-2 sm:mb-3 px-0.5">
      <div className="flex items-center gap-0.5 sm:gap-1 min-w-0">
        <button
          onClick={() => onNavigate('PREV')}
          className="p-1.5 rounded-md text-brand-text-muted hover:text-brand-text hover:bg-brand-surface-2 transition-colors"
          aria-label="Vorheriger Zeitraum"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2} />
        </button>
        <button
          onClick={() => onNavigate('TODAY')}
          className="px-2 sm:px-2.5 py-1 text-xs font-medium rounded-md border border-brand-border text-brand-text hover:bg-brand-surface-2 transition-colors"
        >
          Heute
        </button>
        <button
          onClick={() => onNavigate('NEXT')}
          className="p-1.5 rounded-md text-brand-text-muted hover:text-brand-text hover:bg-brand-surface-2 transition-colors"
          aria-label="Nächster Zeitraum"
        >
          <ChevronRight className="h-4 w-4" strokeWidth={2} />
        </button>
        <span className="ml-1.5 sm:ml-3 text-xs sm:text-sm font-semibold text-brand-text truncate">
          {label}
        </span>
      </div>

      <div className="inline-flex items-center gap-0.5 rounded-lg border border-brand-border bg-brand-surface p-0.5 shrink-0">
        {viewBtn(Views.MONTH, 'Monat', 'M')}
        {viewBtn(Views.WEEK, 'Woche', 'W')}
        {viewBtn(Views.DAY, 'Tag', 'T')}
      </div>
    </div>
  );
};

const CalendarEventContent: React.FC<{ event: MergedEvent }> = ({ event }) => {
  const start = moment(event.start).format('HH:mm');
  if (event.kind === 'google') {
    return (
      <div className="flex items-center gap-1 leading-tight overflow-hidden">
        <span className="font-medium tabular-nums shrink-0 opacity-75">{start}</span>
        <span className="truncate">{event.title}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 leading-tight overflow-hidden">
      <span className="font-semibold tabular-nums shrink-0">{start}</span>
      <span className="truncate">{event.title}</span>
    </div>
  );
};

const CalendarDayHeader: React.FC<any> = ({ date }) => {
  const isToday = moment(date).isSame(moment(), 'day');
  return (
    <div className={`flex flex-col items-center justify-center py-0.5 leading-tight ${isToday ? 'text-brand-primary' : 'text-brand-text-muted'}`}>
      <span className="text-[10px] font-medium uppercase tracking-wider">
        {moment(date).format('dd')}
      </span>
      <span className={`text-sm sm:text-base font-semibold ${isToday ? 'text-brand-primary' : 'text-brand-text'}`}>
        {moment(date).format('D')}
      </span>
    </div>
  );
};

const calendarComponents = {
  toolbar: CalendarToolbar,
  event: CalendarEventContent as any,
  week: { header: CalendarDayHeader },
  day: { header: CalendarDayHeader },
};

const CalendarView: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [googleEvents, setGoogleEvents] = useState<GoogleCalendarEvent[]>([]);
  const [events, setEvents] = useState<MergedEvent[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [selectedGoogleEvent, setSelectedGoogleEvent] = useState<GoogleCalendarEvent | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [debugLoading, setDebugLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [showShareForm, setShowShareForm] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [calendarDate, setCalendarDate] = useState<Date>(() => {
    const d = searchParams.get('date');
    return d ? new Date(`${d}T00:00:00`) : new Date();
  });
  const [calendarView, setCalendarView] = useState<View>(Views.WEEK);

  useEffect(() => {
    loadAppointments();
  }, []);

  // Auto-open the appointment if ?id= is provided
  useEffect(() => {
    const id = searchParams.get('id');
    if (id && appointments.length > 0 && !isNaN(parseInt(id, 10))) {
      const target = appointments.find(a => a.id === parseInt(id));
      if (target) {
        setSelectedAppointment(target);
        setShowModal(true);
        // Clear params so back button works as expected
        searchParams.delete('id');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [appointments, searchParams, setSearchParams]);

  useEffect(() => {
    // Merge app-appointments and Google-only events.
    // Dedupe: a Google event whose extendedProperties point to an existing app-appointment
    // (or whose eventId matches an appointment's google_calendar_event_id) is *not* rendered
    // separately — the app-appointment is the source of truth for it.
    const apptEventIds = new Set(appointments.map(a => a.google_calendar_event_id).filter(Boolean) as string[]);
    const apptIds = new Set(appointments.map(a => String(a.id)));

    const apptEvents: AppointmentEvent[] = appointments.map(appointment => {
      const startDateTime = new Date(`${appointment.appointment_date}T${appointment.start_time}`);
      const endDateTime = new Date(`${appointment.appointment_date}T${appointment.end_time}`);
      return {
        id: `appt-${appointment.id}`,
        kind: 'appt',
        title: `${appointment.patient_name} - ${appointment.procedure_name}`,
        start: startDateTime,
        end: endDateTime,
        resource: appointment,
      };
    });

    const googleOnly: GoogleEvent[] = googleEvents
      .filter(g => !apptEventIds.has(g.id))
      .filter(g => !g.appointmentId || !apptIds.has(g.appointmentId))
      .filter(g => g.start?.dateTime || g.start?.date)
      .map(g => {
        const startSrc = g.start?.dateTime || g.start?.date!;
        const endSrc = g.end?.dateTime || g.end?.date || startSrc;
        return {
          id: `gcal-${g.id}`,
          kind: 'google' as const,
          title: g.summary || '(ohne Titel)',
          start: new Date(startSrc),
          end: new Date(endSrc),
          gevent: g,
        };
      });

    setEvents([...apptEvents, ...googleOnly]);
  }, [appointments, googleEvents]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      const from = moment().subtract(30, 'days').toISOString();
      const to = moment().add(120, 'days').toISOString();
      const [apptResp, gevResp] = await Promise.allSettled([
        apiService.getAppointments(),
        apiService.listGoogleEvents(from, to),
      ]);
      if (apptResp.status === 'fulfilled') {
        setAppointments(apptResp.value.data.data || []);
      } else {
        console.error('Error loading appointments:', apptResp.reason);
        setError('Failed to load appointments');
      }
      if (gevResp.status === 'fulfilled') {
        setGoogleEvents(gevResp.value.data.data || []);
      } else {
        // Don't surface as a hard error — google might be unavailable, but app-termins still work.
        console.warn('Could not load Google Calendar events:', gevResp.reason);
        setGoogleEvents([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = (event: MergedEvent) => {
    if (event.kind === 'appt') {
      setSelectedAppointment(event.resource);
      setShowModal(true);
    } else {
      setSelectedGoogleEvent(event.gevent);
      setShowGoogleModal(true);
    }
  };

  const handleCloseGoogleModal = () => {
    setShowGoogleModal(false);
    setSelectedGoogleEvent(null);
  };

  const handleGoogleEventChanged = async () => {
    await loadAppointments();
  };

  const handleSelectSlot = (slotInfo: any) => {
    // Create a new appointment for the selected slot
    const newAppointment: Partial<Appointment> = {
      id: 0,
      patient_name: '',
      patient_email: '',
      patient_phone: '',
      appointment_date: moment(slotInfo.start).format('YYYY-MM-DD'),
      start_time: moment(slotInfo.start).format('HH:mm'),
      end_time: moment(slotInfo.end).format('HH:mm'),
      provider_id: 0,
      provider_name: '',
      procedure_id: 0,
      procedure_name: '',
      operatory_id: 0,
      operatory_name: '',
      status: 'scheduled' as const,
      notes: '',
      language: 'en',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    setEditingAppointment(newAppointment as Appointment);
    setShowForm(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAppointment(null);
  };

  const handleEditAppointment = () => {
    if (selectedAppointment) {
      setEditingAppointment(selectedAppointment);
      setShowForm(true);
      setShowModal(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingAppointment(null);
  };

  const handleSaveAppointment = async () => {
    await loadAppointments();
    handleCloseForm();
  };

  const handleUpdateAppointment = async () => {
    // Modal bleibt offen — der Admin soll den frischen Status sehen und ggf.
    // direkt weitere Aktionen (Erfassungsbogen-Link, Abschließen, …) auslösen.
    // Wir refetchen die Liste und ziehen den aktualisierten Termin per ID
    // wieder in den Modal-State.
    const currentId = selectedAppointment?.id;
    try {
      const resp = await apiService.getAppointments();
      const list: Appointment[] = resp.data.data || [];
      setAppointments(list);
      if (currentId != null) {
        const fresh = list.find(a => a.id === currentId);
        if (fresh) {
          setSelectedAppointment(fresh);
        } else {
          // Termin wurde gelöscht — Modal schließen.
          handleCloseModal();
        }
      }
    } catch (err) {
      console.error('Reload nach Update fehlgeschlagen:', err);
    }
  };

  const handleGoogleCalendarSync = async () => {
    try {
      setSyncLoading(true);
      await apiService.syncGoogleCalendar();
      await loadAppointments();
      alert('Google Calendar sync completed successfully!');
    } catch (err) {
      console.error('Error syncing Google Calendar:', err);
      alert('Failed to sync with Google Calendar');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleDebugGoogleCalendar = async () => {
    try {
      setDebugLoading(true);
      console.log('🔍 DEBUG: Fetching raw Google Calendar events...');
      
      const response = await apiService.debugGoogleCalendarEvents();
      
      console.log('🎉 DEBUG: Raw Google Calendar Response:');
      console.log('📊 Response Status:', response.status);
      console.log('📋 Response Data:', response.data);
      
      if (response.data.success) {
        console.log(`✅ Found ${response.data.events.length} events in Google Calendar`);
        console.log('📅 Calendar ID:', response.data.calendarId);
        console.log('🔐 Is Authenticated:', response.data.isAuthenticated);
        
        // Log each event in detail
        response.data.events.forEach((event: any, index: number) => {
          console.log(`\n📅 Event ${index + 1}:`);
          console.log('  🏷️ Title:', event.summary);
          console.log('  🆔 ID:', event.id);
          console.log('  ⏰ Start:', event.start);
          console.log('  ⏱️ End:', event.end);
          console.log('  📝 Description:', event.description?.substring(0, 200) + '...');
          console.log('  🔗 HTML Link:', event.htmlLink);
        });
        
        alert(`🎉 Debug complete! Found ${response.data.events.length} events. Check browser console for details.`);
      } else {
        console.error('❌ Debug failed:', response.data.error);
        alert(`❌ Debug failed: ${response.data.error}`);
      }
    } catch (err: any) {
      console.error('❌ Error debugging Google Calendar:', err);
      console.error('❌ Error details:', err.response?.data);
      alert(`❌ Debug error: ${err.message}`);
    } finally {
      setDebugLoading(false);
    }
  };

  const handleShareGoogleCalendar = async () => {
    if (!shareEmail.trim()) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      setShareLoading(true);
      console.log('🔗 Sharing calendar with:', shareEmail);
      
      const response = await apiService.shareGoogleCalendar(shareEmail, 'reader');
      
      console.log('✅ Calendar sharing response:', response.data);
      
      if (response.data.success) {
        alert(`✅ Calendar shared successfully with ${shareEmail}! They can now view events in their Google Calendar.`);
        setShareEmail('');
        setShowShareForm(false);
      } else {
        console.error('❌ Sharing failed:', response.data.error);
        alert(`❌ Sharing failed: ${response.data.error}`);
      }
    } catch (err: any) {
      console.error('❌ Error sharing calendar:', err);
      console.error('❌ Error details:', err.response?.data);
      
      // Provide more detailed error messages
      if (err.response?.status === 409) {
        alert(`📋 User ${shareEmail} already has access to the calendar.`);
      } else {
        alert(`❌ Sharing error: ${err.message}`);
      }
    } finally {
      setShareLoading(false);
    }
  };

  const eventStyleGetter = (event: MergedEvent) => {
    if (event.kind === 'google') {
      // Google-only event — distinct visual: light gray, dotted outline, no status color.
      const isCancelled = event.gevent.status === 'cancelled';
      const style: React.CSSProperties = {
        backgroundColor: isCancelled ? '#fee2e2' : '#e5e7eb',
        borderRadius: '5px',
        opacity: 0.85,
        color: isCancelled ? '#991b1b' : '#374151',
        border: '1.5px dotted #9ca3af',
        display: 'block',
        textDecoration: isCancelled ? 'line-through' : 'none',
      };
      return { style, className: 'google-only-event' };
    }

    const appointment = event.resource;
    let backgroundColor = '#3174ad';

    switch (appointment.status) {
      case 'pending':
        backgroundColor = '#a855f7'; // purple - distinct from scheduled
        break;
      case 'confirmed':
        backgroundColor = '#10b981';
        break;
      case 'scheduled':
        backgroundColor = '#f59e0b';
        break;
      case 'completed':
        backgroundColor = '#3b82f6';
        break;
      case 'cancelled':
        backgroundColor = '#ef4444';
        break;
      case 'no_show':
        backgroundColor = '#6b7280';
        break;
    }

    const isWebsiteBooking = (appointment as any).source === 'website';
    const style: React.CSSProperties = {
      backgroundColor,
      borderRadius: '5px',
      opacity: 0.85,
      color: 'white',
      border: isWebsiteBooking ? '2px dashed #fbbf24' : '0px',
      display: 'block',
    };

    return { style, className: isWebsiteBooking ? 'website-booking' : '' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  const iconActions = (
    <>
      <button
        onClick={handleGoogleCalendarSync}
        disabled={syncLoading}
        className="inline-flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 text-sm font-medium rounded-md text-brand-text border border-brand-border bg-brand-surface hover:bg-brand-surface-2 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 disabled:opacity-50"
        title="Google Kalender synchronisieren"
        aria-label="Google Kalender synchronisieren"
      >
        <RefreshCw className={`h-4 w-4 ${syncLoading ? 'animate-spin' : ''}`} strokeWidth={1.75} aria-hidden />
        <span className="hidden sm:inline">{syncLoading ? 'Sync…' : 'Sync'}</span>
      </button>
      <button
        onClick={handleDebugGoogleCalendar}
        disabled={debugLoading}
        className="inline-flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 text-sm font-medium rounded-md text-brand-text border border-brand-border bg-brand-surface hover:bg-brand-surface-2 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 disabled:opacity-50"
        title="Google Events debuggen"
        aria-label="Google Events debuggen"
      >
        <Bug className="h-4 w-4" strokeWidth={1.75} aria-hidden />
        <span className="hidden sm:inline">{debugLoading ? 'Debug…' : 'Debug'}</span>
      </button>
      <button
        onClick={() => setShowShareForm(!showShareForm)}
        className="inline-flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 text-sm font-medium rounded-md text-brand-text border border-brand-border bg-brand-surface hover:bg-brand-surface-2 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
        title="Kalender freigeben"
        aria-label="Kalender freigeben"
      >
        <Share2 className="h-4 w-4" strokeWidth={1.75} aria-hidden />
        <span className="hidden sm:inline">Freigeben</span>
      </button>
    </>
  );

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Desktop header */}
      <div className="hidden sm:block">
        <PageHeader
          eyebrow="Übersicht"
          title="Kalender"
          description="Termine planen, Google Kalender synchronisieren"
          actions={
            <div className="flex flex-wrap gap-2 justify-end">
              {iconActions}
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary-hover shadow-sm transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
              >
                <Plus className="h-4 w-4" strokeWidth={2} aria-hidden />
                Neuer Termin
              </button>
            </div>
          }
        />
      </div>

      {/* Mobile header — compact single row */}
      <div className="sm:hidden flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold tracking-tight text-brand-text truncate">Kalender</h2>
        <div className="flex items-center gap-1.5 shrink-0">{iconActions}</div>
      </div>

      {error && (
        <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 px-4 py-3 text-sm font-medium text-rose-800 dark:text-rose-300">
          {error}
        </div>
      )}

      {/* Calendar Sharing Form */}
      {showShareForm && (
        <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-4">
          <div className="flex flex-col gap-3">
            <div>
              <h3 className="text-sm font-semibold text-brand-text">Google Kalender freigeben</h3>
              <p className="text-xs text-brand-text-muted mt-1">
                Service-Account-Kalender mit deinem Google-Konto teilen, um Events direkt in Google Kalender zu sehen.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                placeholder="Deine Google-E-Mail-Adresse"
                className="flex-1 px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleShareGoogleCalendar}
                  disabled={shareLoading || !shareEmail.trim()}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary-hover shadow-sm transition-colors disabled:opacity-50 cursor-pointer"
                >
                  {shareLoading ? 'Freigeben…' : 'Freigeben'}
                </button>
                <button
                  onClick={() => setShowShareForm(false)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md text-brand-text border border-brand-border hover:bg-brand-surface-2 transition-colors cursor-pointer"
                >
                  <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calendar */}
      <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-2 sm:p-4 h-[calc(100vh-220px)] sm:h-[calc(100vh-260px)] min-h-[520px] flex flex-col">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ flex: 1, minHeight: 0 }}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          selectable
          views={[Views.MONTH, Views.WEEK, Views.DAY]}
          date={calendarDate}
          onNavigate={d => setCalendarDate(d)}
          view={calendarView}
          onView={v => setCalendarView(v)}
          step={30}
          timeslots={2}
          min={moment().startOf('day').add(7, 'hours').toDate()}
          max={moment().startOf('day').add(21, 'hours').toDate()}
          scrollToTime={moment().startOf('day').add(8, 'hours').toDate()}
          showMultiDayTimes
          eventPropGetter={eventStyleGetter}
          components={calendarComponents}
          formats={{
            timeGutterFormat: 'HH:mm',
            eventTimeRangeFormat: ({ start, end }) => {
              return `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`;
            }
          }}
        />
      </div>

      {/* Legend */}
      <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-4">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted mb-2">Statusübersicht</h3>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          <LegendDot color="#a855f7" label="Offen (Website)" />
          <LegendDot color="#eab308" label="Geplant" />
          <LegendDot color="#22c55e" label="Bestätigt" />
          <LegendDot color="#3b82f6" label="Abgeschlossen" />
          <LegendDot color="#ef4444" label="Storniert" />
          <LegendDot color="#6b7280" label="Nicht erschienen" />
          <div className="flex items-center gap-1.5 text-xs text-brand-text">
            <span aria-hidden className="inline-block w-4 h-0 border-t-2 border-dashed" style={{ borderColor: '#fbbf24' }} />
            <span>Gestrichelter Rand = Buchung über seadent.es</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-brand-text">
            <span aria-hidden className="inline-block w-3 h-3 rounded-sm border border-dotted" style={{ backgroundColor: '#e5e7eb', borderColor: '#9ca3af' }} />
            <span>Grau gepunktet = Event nur in Google Calendar</span>
          </div>
        </div>
      </div>

      {/* Mobile FAB — Neuer Termin */}
      <button
        onClick={() => setShowForm(true)}
        className="sm:hidden fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 pl-4 pr-5 py-4 text-sm font-semibold rounded-full text-white bg-brand-primary hover:bg-brand-primary-hover shadow-lg shadow-brand-primary/30 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 active:scale-95"
        aria-label="Neuer Termin"
      >
        <Plus className="h-5 w-5" strokeWidth={2.25} aria-hidden />
        <span>Neuer Termin</span>
      </button>

      {/* Appointment Modal */}
      {showModal && selectedAppointment && (
        <AppointmentModal
          appointment={selectedAppointment}
          onClose={handleCloseModal}
          onUpdate={handleUpdateAppointment}
          onEdit={handleEditAppointment}
        />
      )}

      {/* Appointment Form */}
      {showForm && (
        <AppointmentForm
          appointment={editingAppointment || undefined}
          onClose={handleCloseForm}
          onSave={handleSaveAppointment}
        />
      )}

      {/* Google Event Modal */}
      {showGoogleModal && selectedGoogleEvent && (
        <GoogleEventModal
          event={selectedGoogleEvent}
          onClose={handleCloseGoogleModal}
          onChange={handleGoogleEventChanged}
        />
      )}
    </div>
  );
};

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div className="flex items-center gap-1.5 text-xs text-brand-text">
    <span aria-hidden className="inline-block w-3 h-3 rounded-sm ring-1 ring-black/5" style={{ backgroundColor: color }} />
    <span>{label}</span>
  </div>
);

export default CalendarView;
